import uniqueId from 'lodash/uniqueId'
import { StrictMode, useCallback, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent, VFC } from 'react'
import { Tab } from '../Tab'
import * as styles from './styles'
import { TabDirection, TabLabelPosition, TabSize } from './types'
import type { TabItem, TabsProps } from './types'

export { TabDirection, TabLabelPosition, TabSize }

export type { TabItem }

function getIndexOfValue(list: TabItem[], value: string): number {
  return list.findIndex((item) => item.value === value)
}

/**
 * The standard Gem Design System tab set. It handles keyboard navigation and
 * other accessibility concerns.
 *
 * @note If you use this component in parallel with the Mousetrap library to
 * bind keyboard events you’ll need to adjust the Mousetrap callback(s) to
 * ignore any keyboard events that originate within this component’s `class`
 * (`.lapidary-tabs`); otherwise Mousetrap will trigger for the `keydown` event
 * this component binds.
 *
 * @example
 * <Tabs
 *   defaultValue={value}
 *   onChange={(newValue: string) => setValue(newValue)}
 *   tabList={[
 *     { label: 'Day', value: Timescale.Day },
 *     { label: 'Week', value: Timescale.Week },
 *   ]}
 *   {...{
 *     theme,
 *   }}
 * />
 */
export const Tabs: VFC<TabsProps> & { tabsRole: string } = ({
  defaultValue,
  direction = TabDirection.Row,
  disabled,
  displayLabel = true,
  htmlId,
  label,
  labelPosition = TabLabelPosition.End,
  onChange,
  size = TabSize.Regular,
  styleOverrides = {},
  tabList,
  theme = 'light',
}) => {
  const count = tabList.length
  const listRef = useRef<HTMLDivElement>(null)
  const managedHtmlId = useRef<string>(htmlId || uniqueId('lapidary-tabs'))

  const defaultIndex = defaultValue ? getIndexOfValue(tabList, defaultValue) : 0
  const [activeIndex, setActiveIndex] = useState<number>(defaultIndex)

  const handleClick = useCallback(
    (_: MouseEvent<HTMLButtonElement>, index: number): void => {
      if (index === activeIndex) return

      onChange(tabList[index]?.value)
      setActiveIndex(index)
    },
    [activeIndex, onChange, tabList]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
      if (
        !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
      )
        return

      event.stopPropagation()
      event.preventDefault()

      const maximumValidIndex = count === 0 ? 0 : count - 1

      let nextFocusIndex: number
      if (['ArrowLeft', 'ArrowUp'].includes(event.key)) {
        nextFocusIndex = index - 1
        if (nextFocusIndex < 0) nextFocusIndex = maximumValidIndex
      } else {
        nextFocusIndex = index + 1
        if (nextFocusIndex > maximumValidIndex) nextFocusIndex = 0
      }

      // The obvious approach here is to use `children.item(n)` (or
      // `children[n]`), but that returns an `Element`, which doesn't support the
      // `focus` method.
      //
      // Using `querySelector` means this relies on `<Tab>`'s outermost
      // element being `<button>`.
      const children = listRef.current?.querySelectorAll('button')
      if (children) {
        const focusingElement = Array.from(children)[nextFocusIndex]
        focusingElement.focus()
      }
    },
    [count]
  )

  return (
    <StrictMode>
      <div
        aria-label={label}
        // This class is only declared so Marionette code can ignore events that
        // originate from this component. It should *not* be used for styling:
        // pass `styleOverrides` instead.
        className="lapidary-tabs"
        css={[styles.container({ direction, theme }), styleOverrides.container]}
        id={managedHtmlId.current}
        ref={listRef}
        role="tablist"
        tabIndex={-1}
      >
        {tabList.map((item, index) => {
          const selected = index === activeIndex

          return (
            <Tab
              key={item.value}
              onClick={(event): void => handleClick(event, index)}
              onKeyDown={(event): void => handleKeyDown(event, index)}
              tabIndex={selected ? 0 : -1}
              tabCount={count}
              {...item}
              {...{
                direction,
                disabled,
                displayLabel,
                labelPosition,
                selected,
                size,
                theme,
              }}
            />
          )
        })}
      </div>
    </StrictMode>
  )
}
Tabs.displayName = 'Lapidary Unstable: Tabs'
// magic for `react-tabs`
Tabs.tabsRole = 'Tabs'
